import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ui/ErrorBoundary';
import AppShell from './components/layout/AppShell';
import ColdLeads from './pages/ColdLeads';
import WarmLeads from './pages/WarmLeads';
import Contacts from './pages/Contacts';
import Settings from './pages/Settings';
import Login from './pages/Login';
import { useAuth } from './hooks/useAuth';
import { useAppStore } from './stores/appStore';
import { isSupabaseConfigured, supabase } from './lib/supabase';

function AuthGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const user = useAppStore(s => s.user);
  const [checking, setChecking] = useState(isSupabaseConfigured);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    supabase.auth.getSession().then(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-dx-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-dx-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isSupabaseConfigured && !user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App(): React.JSX.Element {
  useAuth();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <AuthGate>
                <AppShell />
              </AuthGate>
            }
          >
            <Route index element={<Navigate to="/cold-leads" replace />} />
            <Route path="cold-leads" element={<ColdLeads />} />
            <Route path="warm-leads" element={<WarmLeads />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
        <Toaster position="bottom-right" theme="dark" richColors />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
